/**
 * Display names for the clinic Versorgungslevel codes carried by
 * `GET /clinics/{clinicSn}/metadata` as the raw `versorgungslevel` string.
 *
 * The codes and their German names mirror the backend catalog
 * (`GET /clinics/levels/available/`). They are inlined rather than fetched
 * because the values are a closed, rarely-changing set and the consumers
 * (e.g. the clinic hero band) render above the fold — resolving one label must
 * not cost an extra request.
 *
 * `short` is the compact form for chips and table cells; `full` is the
 * catalog's own wording and belongs in a tooltip or detail field. Unknown codes
 * have no entry, so callers fall back to the raw code.
 */
export interface AysClinicVersorgungslevelLabel {
  short: string;
  full: string;
}

/** Keys are the backend's catalog ids, matched case-insensitively (`L3u`/`L3U`). */
export const AYS_CLINIC_VERSORGUNGSLEVEL_DISPLAY_NAMES: Record<
  string,
  AysClinicVersorgungslevelLabel
> = {
  l1i: {
    short: 'Level Ii',
    full: 'Level Ii (integrierte ambulant/stationäre Versorgung)',
  },
  l1n: { short: 'Level In', full: 'Level In (mit Notfallstufe I)' },
  l2: { short: 'Level II', full: 'Level II' },
  l3: { short: 'Level III', full: 'Level III' },
  l3u: { short: 'Level IIIU', full: 'Level IIIU (Universitätsklinik)' },
  fa: { short: 'Fachklinik', full: 'Fachklinik' },
};

/**
 * Resolve a raw `versorgungslevel` code to its display names. Unknown or empty
 * codes resolve to the trimmed raw code in both forms, so a level the catalog
 * does not (yet) know is still shown rather than silently dropped.
 */
export function aysClinicVersorgungslevelLabel(
  code: string | undefined | null,
): AysClinicVersorgungslevelLabel | undefined {
  const raw = code?.trim();

  if (!raw) {
    return undefined;
  }

  return (
    AYS_CLINIC_VERSORGUNGSLEVEL_DISPLAY_NAMES[raw.toLowerCase()] ?? {
      short: raw,
      full: raw,
    }
  );
}
